import { z } from 'zod';
import { BOARD_QUESTION_IDS, boardCoverage, boardSchema, type Board } from './board';

/* ─────────────────────────────────────────────────────────────────────
   Setup — os 6 blocos do onboarding.
   Um schema por bloco: o mesmo arquivo valida o formulário e a rota.
   Campos vazios são string vazia, nunca null — o formulário é sempre
   controlado e o cálculo de prontidão só precisa de `.trim()`.
   ───────────────────────────────────────────────────────────────────── */

const text = (max = 200) => z.string().trim().max(max).default('');

export const SEGMENTOS = [
  'Educação', 'Saúde', 'Serviços profissionais', 'Indústria',
  'Varejo', 'Terceiro setor', 'Tecnologia',
] as const;

export const OPERACOES = ['B2B', 'B2C', 'B2G'] as const;
export type Operacao = (typeof OPERACOES)[number];
export const COLABORADORES = ['1 – 20', '21 – 80', '81 – 250', '251 – 1.000', 'Mais de 1.000'] as const;
export const FATURAMENTO = ['Até R$ 1 mi', 'R$ 1 mi – R$ 10 mi', 'R$ 10 mi – R$ 50 mi', 'Acima de R$ 50 mi'] as const;
export const CLIENTES = ['Até 200', '200 – 800', '800 – 1.500', 'Acima de 1.500'] as const;
export const MATURIDADE = ['Inicial', 'Em estruturação', 'Consolidada', 'Referência no setor'] as const;

const oneOf = <T extends readonly [string, ...string[]]>(values: T) =>
  z.union([z.enum(values), z.literal('')]).default('');

/** Tipo de operação: uma ou mais (B2B e B2C, por exemplo). Aceita o valor
    antigo, de escolha única ("B2B"), e devolve sempre a lista em ordem. */
const operacoes = z.preprocess(
  v => (v === undefined || v === null || v === '' ? [] : typeof v === 'string' ? [v] : v),
  z.array(z.enum(OPERACOES)).max(OPERACOES.length),
).transform(xs => OPERACOES.filter(o => xs.includes(o)));

/** Horizonte e focos do objetivo: o ADS sugere, a organização ajusta. */
export const HORIZONTES = ['6 meses', '1 ano', '18 meses', '2 anos', '3 anos', '5 anos', '10 anos'] as const;
export const FOCOS = [
  'Posicionamento', 'Crescimento', 'Inovação', 'Experiência do cliente', 'Eficiência',
  'Pessoas e cultura', 'Governança', 'Expansão', 'Transformação digital', 'Sustentabilidade', 'Reputação',
] as const;
export type Horizonte = (typeof HORIZONTES)[number];
export type Foco = (typeof FOCOS)[number];

export const identificacaoSchema = z.object({
  org:  text(120),
  site: text(200),
  loc:  text(120),
  seg:  oneOf(SEGMENTOS),
});

export const perfilSchema = z.object({
  op:   operacoes,
  team: oneOf(COLABORADORES),
  rev:  oneOf(FATURAMENTO),
  cli:  oneOf(CLIENTES),
  mat:  oneOf(MATURIDADE),
});

export const canaisSchema = z.object({
  instagram: text(300),
  linkedin:  text(300),
  youtube:   text(300),
  extra: z.array(z.object({ label: text(60), url: text(300) })).max(8).default([]),
});

export const objetivoSchema = z.object({
  goal:   text(500),
  /* preenchidos pela interpretação do ADS; editáveis */
  horizon: oneOf(HORIZONTES),
  focus:   oneOf(FOCOS),
  focus2:  oneOf(FOCOS),
});

export const materiaisSchema = z.object({
  uploads: z.array(z.uuid()).max(40).default([]),
});

export const SETUP_SCHEMAS = {
  identificacao: identificacaoSchema,
  perfil:        perfilSchema,
  canais:        canaisSchema,
  objetivo:      objetivoSchema,
  materiais:     materiaisSchema,
  board:         boardSchema,
} as const;

export type SetupBlockKey = keyof typeof SETUP_SCHEMAS;
export const SETUP_BLOCK_KEYS = Object.keys(SETUP_SCHEMAS) as SetupBlockKey[];
export const setupBlockKeySchema = z.enum(SETUP_BLOCK_KEYS as [SetupBlockKey, ...SetupBlockKey[]]);

export type Identificacao = z.infer<typeof identificacaoSchema>;
export type Perfil        = z.infer<typeof perfilSchema>;
export type Canais        = z.infer<typeof canaisSchema>;
export type Objetivo      = z.infer<typeof objetivoSchema>;
export type Materiais     = z.infer<typeof materiaisSchema>;

export interface SetupAnswers {
  identificacao: Identificacao;
  perfil:        Perfil;
  canais:        Canais;
  objetivo:      Objetivo;
  materiais:     Materiais;
  board:         Board;
}

/** Os blocos como aparecem na tela: número, título e quais campos contam. */
export const SETUP_BLOCKS: ReadonlyArray<{
  key: SetupBlockKey;
  no: string;
  title: string;
  /** campos que contam para a prontidão; vazio = bloco opcional */
  fields: readonly string[];
  /** peso do bloco na leitura de prontidão (soma = 1) */
  weight: number;
}> = [
  { key: 'identificacao', no: '01', title: 'Identificação da organização', fields: ['org', 'site', 'loc', 'seg'], weight: .30 },
  { key: 'perfil',        no: '02', title: 'Perfil do negócio',            fields: ['op', 'team', 'rev', 'cli', 'mat'], weight: .22 },
  { key: 'canais',        no: '03', title: 'Canais e presença digital',    fields: ['instagram', 'linkedin', 'youtube'], weight: .10 },
  { key: 'objetivo',      no: '04', title: 'Objetivo e contexto',          fields: ['goal', 'horizon', 'focus', 'focus2'], weight: .20 },
  { key: 'materiais',     no: '05', title: 'Documentos e materiais',       fields: [], weight: .04 },
  /* as 16 perguntas da entrevista: conta a pergunta respondida por alguém da liderança */
  { key: 'board',         no: '06', title: 'Leitura executiva do Board',   fields: BOARD_QUESTION_IDS, weight: .14 },
];

export const emptySetup = (): SetupAnswers => ({
  identificacao: identificacaoSchema.parse({}),
  perfil:        perfilSchema.parse({}),
  canais:        canaisSchema.parse({}),
  objetivo:      objetivoSchema.parse({}),
  materiais:     materiaisSchema.parse({}),
  board:         boardSchema.parse({}),
});

/** Quantos campos de um bloco estão preenchidos. */
export function blockFilled(key: SetupBlockKey, data: Record<string, unknown>): { done: number; total: number } {
  if (key === 'board') return boardCoverage({ entrevistados: ((data as Partial<Board> | null)?.entrevistados ?? []) as Board['entrevistados'] });
  const meta = SETUP_BLOCKS.find(b => b.key === key)!;
  const filled = (v: unknown) => (Array.isArray(v) ? v.length > 0 : String(v ?? '').trim().length > 0);
  const done = meta.fields.filter(f => filled(data[f])).length;
  return { done, total: meta.fields.length };
}

/**
 * Prontidão do setup, de 0 a 100 — a mesma conta do instrumento do Início.
 * Bloco opcional (sem campos) conta como completo.
 */
export function setupReadiness(answers: SetupAnswers): { pct: number; fill: number[]; complete: number } {
  const fill = SETUP_BLOCKS.map(b => {
    if (!b.fields.length) return 1;
    const { done, total } = blockFilled(b.key, answers[b.key] as Record<string, unknown>);
    return done / total;
  });
  const pct = Math.round(fill.reduce((s, v, i) => s + v * SETUP_BLOCKS[i]!.weight, 0) * 100);
  const complete = fill.filter((v, i) => SETUP_BLOCKS[i]!.fields.length > 0 && v > .999).length;
  return { pct, fill, complete };
}
